import React, { useState, useEffect, useRef } from 'react'
import useMpciToast from '../Common/hooks/useMpciToast'
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, makeStyles,
  Menu, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, useTheme
} from '@material-ui/core'
import { fade } from '@material-ui/core/styles'
import { BasePage as CngBasePage } from 'cng-web-lib'
import MpciApiClient from '../Common/services/MpciApiClient'
import useBreadcrumbNameMap from 'src/useBreadcrumbNameMap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import useOrgFilter from '../Common/hooks/useOrgFilter'



const useChipStyles = makeStyles((theme) => ({
  base: { fontWeight: 700, fontSize: 12, borderRadius: 20 },
  completed: { backgroundColor: fade(theme.palette.success.main, 0.15), color: theme.palette.success.dark },
  processing: { backgroundColor: fade(theme.palette.warning.main, 0.15), color: theme.palette.warning.dark },
  failed: { backgroundColor: fade(theme.palette.error.main, 0.15), color: theme.palette.error.dark },
  received: { backgroundColor: fade(theme.palette.info.main, 0.15), color: theme.palette.info.dark },
  retrying: { backgroundColor: fade(theme.palette.warning.main, 0.20), color: theme.palette.warning.dark }
}))

const BILL_TYPE_OPTIONS = [
  { value: 'M', label: 'Master Bill' },
  { value: 'H', label: 'House Bill' }
]

const EXPORT_FORMAT_OPTIONS = [
  { value: 'excel', label: 'Excel' },
  { value: 'csv', label: 'CSV' }
]

const FILING_STATUS_OPTIONS = [
  { value: 'status', label: 'Status' },
  { value: 'sub-Status', label: 'Sub-Status' }
]

// Poll interval in ms
const POLL_INTERVAL = 300000

const INITIAL_FILTERS = {
  creationDateFrom: '',
  creationDateTo: '',
  submissionDateFrom: '',
  submissionDateTo: '',
  exportFormat: '',
  billType: '',
  masterBillNo: '',
  status: ''
}

function ReportsPage({ history, showNotification }) {
  const chip = useChipStyles()
  const theme = useTheme()
  // Org filter — admin sees dropdown + all data, others scoped to own org
  const { isAdmin, orgList, selectedOrgId, setSelectedOrgId, effectiveOrgId } = useOrgFilter()

    const [filters, setFilters] = useState(INITIAL_FILTERS)
    const [reportRequests, setReportRequests] = useState([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
    const [filterSummaryOpen, setFilterSummaryOpen] = useState(false)
  const [filterSummaryData, setFilterSummaryData] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [excelWarnOpen, setExcelWarnOpen] = useState(false)
  const [pendingPayload, setPendingPayload] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const pollRef = useRef(null)
  const mountedRef = useRef(true)
  const pageRef = useRef(page)
  const pageSizeRef = useRef(pageSize)

  useEffect(() => { pageRef.current = page }, [page])
  useEffect(() => { pageSizeRef.current = pageSize }, [pageSize])

  const [menuAnchor, setMenuAnchor] = useState(null)
  const [menuRow, setMenuRow] = useState(null)

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget)
    setMenuRow(row)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
    setMenuRow(null)
  }

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

          // Fetch report request list — POST /mpci/file-download/list
  const fetchReportList = (p = page, ps = pageSize, isManual = false) => {
    if (!mountedRef.current) return
    if (isManual) setRefreshing(true)
    const body = {
      page: p,
      size: ps,
      sortBy: 'createdDate',
      sortDir: 'desc',
      module: 'MPCI_REPORT',
      ...(effectiveOrgId != null && { orgId: effectiveOrgId })
    }
    MpciApiClient.post(
      UaeMpciApiUrls.FILE_DOWNLOAD_LIST,
      body,
      (data) => {
        if (!mountedRef.current) return
        if (isManual) setRefreshing(false)
        setReportRequests(data?.content ?? (Array.isArray(data) ? data : []))
        setTotalElements(data?.totalElements ?? 0)
        setTotalPages(data?.totalPages ?? 0)
      },
      (error) => {
        if (!mountedRef.current) return
        if (isManual) setRefreshing(false)
        console.error('[Reports] fetch list failed', error)
      }
    )
  }


    useEffect(() => {
    fetchReportList(page, pageSize)
  }, [page, pageSize]) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while any record is in RECV/PROCESSING status
  useEffect(() => {
        const hasPending = reportRequests.some((r) => {
      const s = r.status?.toUpperCase()
      return s === 'RECV' || s === 'PROC' || s === 'PROCESSING' || s === 'RETR'
    })
    // Only set interval if not already set
        if (hasPending && !pollRef.current) {
      setIsPolling(true)
      pollRef.current = setInterval(() => {
        if (mountedRef.current) fetchReportList()
      }, POLL_INTERVAL)
    } else if (!hasPending && pollRef.current) {
      setIsPolling(false)
      // Clear interval when no more pending records
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    // Cleanup on unmount
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [reportRequests])

    const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleReset = () => {
    setFilters(INITIAL_FILTERS)
    setSelectedOrgId(null)
  }

  const triggerGenerate = (payload) => {
    setGenerating(true)
    MpciApiClient.post(
      UaeMpciApiUrls.REPORT_GENERATE,
      payload,
      (data) => {
        if (!mountedRef.current) return
        setGenerating(false)
        if (showNotification) showNotification('success', 'Report request submitted. Check the table below for status.')
        if (data) setReportRequests((prev) => [data, ...prev])
        fetchReportList()
      },
      (error) => {
        if (!mountedRef.current) return
        setGenerating(false)
        console.error('[Reports] generate failed', error)
        if (showNotification) showNotification('error', 'Failed to generate report. Please try again.')
      }
    )
  }

  const handleGenerateReport = () => {
    const hasCreationRange = filters.creationDateFrom && filters.creationDateTo
    const hasSubmissionRange = filters.submissionDateFrom && filters.submissionDateTo
    if (!hasCreationRange && !hasSubmissionRange) {
      if (showNotification) showNotification('error', 'Please provide either Creation Date Range or Submission Date Range.')
      return
    }
    if (!filters.exportFormat) {
      if (showNotification) showNotification('error', 'Please select an Export Format.')
      return
    }
    const payload = {
      module: 'MPCI_REPORT',
      exportFormat: filters.exportFormat,
      ...(effectiveOrgId != null && { orgId: effectiveOrgId }),
      ...(filters.billType && { billType: filters.billType }),
      ...(filters.masterBillNo && { masterBillNo: filters.masterBillNo }),
      ...(filters.status && { status: filters.status }),
      ...(hasCreationRange && {
        createdDateStart: filters.creationDateFrom,
        createdDateEnd: filters.creationDateTo
      }),
      ...(hasSubmissionRange && {
        submittedDateStart: filters.submissionDateFrom,
        submittedDateEnd: filters.submissionDateTo
      })
    }
    setGenerating(true)
    MpciApiClient.post(
      UaeMpciApiUrls.REPORT_COUNT,
      payload,
      (data) => {
        if (!mountedRef.current) return
        if (data.count > 1_048_575 && payload.exportFormat === 'excel') {
          setGenerating(false)
          setPendingPayload(payload)
          setExcelWarnOpen(true)
        } else {
          triggerGenerate(payload)
        }
      },
      (error) => {
        if (!mountedRef.current) return
        setGenerating(false)
        console.error('[Reports] count check failed', error)
        if (showNotification) showNotification('error', 'Failed to check record count. Please try again.')
      }
    )
  }

  const handleRegenerate = (row) => {
    handleMenuClose()
    if (showNotification) showNotification('success', 'Re-generate request submitted.')
    MpciApiClient.post(
      `${UaeMpciApiUrls.REPORT_REGENERATE}/${row.id}`,
      null,
      (data) => {
        if (!mountedRef.current) return
        fetchReportList(pageRef.current, pageSizeRef.current, true)
      },
      (error) => {
        if (!mountedRef.current) return
        console.error('[Reports] regenerate failed', error)
        if (showNotification) showNotification('error', 'Re-generate failed. Please try again.')
      }
    )
  }

  const handleRetry = (row) => {
    handleMenuClose()
    // Optimistic update — show queued state immediately without waiting for slow API
    setReportRequests((prev) => prev.map((r) => r.id === row.id ? { ...r, status: 'RETR' } : r))
    if (showNotification) showNotification('success', 'Retry request submitted.')
    MpciApiClient.post(
      `${UaeMpciApiUrls.REPORT_RETRY}/${row.id}`,
      null,
      (data) => {
        if (!mountedRef.current) return
        fetchReportList(pageRef.current, pageSizeRef.current, true)
      },
      (error) => {
        if (!mountedRef.current) return
        console.error('[Reports] retry failed', error)
        // Revert optimistic update on failure
        setReportRequests((prev) => prev.map((r) => r.id === row.id ? { ...r, status: row.status } : r))
        if (showNotification) showNotification('error', 'Retry failed. Please try again.')
      }
    )
  }

      const handleDownload = (row) => {
    const url = `${UaeMpciApiUrls.FILE_DOWNLOAD_GET}/${row.id}`
    fetch(url, {
      method: 'GET',
      headers: { Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')) }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        // Parse filename from Content-Disposition: attachment; filename="..."
        const disposition = res.headers.get('Content-Disposition') || ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        const fileName = match ? match[1] : (row.fileName || row.filename || `report_${row.id}`)
        return res.blob().then((blob) => ({ blob, fileName }))
      })
      .then(({ blob, fileName }) => {
        if (!mountedRef.current) return
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      })
      .catch((err) => {
        if (!mountedRef.current) return
        console.error('[Reports] download failed', err)
        if (showNotification) showNotification('error', 'Download failed. Please try again.')
      })
  }

  const handleDownloadErrorLog = (row) => {
    const url = `${UaeMpciApiUrls.FILE_DOWNLOAD_GET}/${row.id}`
    fetch(url, {
      method: 'GET',
      headers: { Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken')) }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const disposition = res.headers.get('Content-Disposition') || ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        const fileName = match ? match[1] : (row.fileName || `error_log_${row.id}.log`)
        return res.blob().then((blob) => ({ blob, fileName }))
      })
      .then(({ blob, fileName }) => {
        if (!mountedRef.current) return
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      })
      .catch((err) => {
        if (!mountedRef.current) return
        console.error('[Reports] error log download failed', err)
        if (showNotification) showNotification('error', 'Error log download failed. Please try again.')
      })
  }

          function renderStatusChip(status) {
    if (!status) return <Typography variant='body2' color='textSecondary'>—</Typography>
    const s = status.toUpperCase()
    const MAP = {
      COMP: ['Completed', chip.completed],
      COMPLETED: ['Completed', chip.completed],
      FAIL: ['Failed', chip.failed],
      FAILED: ['Failed', chip.failed],
      PROC: ['Processing', chip.processing],
      PROCESSING: ['Processing', chip.processing],
      RECV: ['Received', chip.received],
      RETR: ['Processing - Retrying', chip.retrying]
    }
    const match = MAP[s]
    if (match) return <Chip label={match[0]} size='small' className={`${chip.base} ${match[1]}`} />
    return <Typography variant='body2' color='textSecondary'>{status}</Typography>
  }

  function renderDownloadCell(request, onDownload, onDownloadErrorLog) {
    const s = request.status?.toUpperCase()

    if (s === 'COMP' || s === 'COMPLETED') {
      return (
        <Box
          display='flex'
          alignItems='center'
          style={{ gap: 5, cursor: 'pointer' }}
          onClick={() => onDownload(request)}
        >
          <FontAwesomeIcon icon={['fal', 'download']} style={{ fontSize: 13 }} />
          <Typography variant='body2' style={{ textDecoration: 'underline' }}>
            Download
          </Typography>
        </Box>
      )
    }

    if ((s === 'FAIL' || s === 'FAILED') && request.filePath) {
      return (
        <Box
          display='flex'
          alignItems='center'
          style={{ gap: 5, cursor: 'pointer', color: 'inherit' }}
          onClick={() => onDownloadErrorLog(request)}
        >
          <FontAwesomeIcon icon={['fal', 'file-exclamation']} style={{ fontSize: 13 }} />
          <Typography variant='body2' style={{ textDecoration: 'underline' }}>
            Error Log
          </Typography>
        </Box>
      )
    }

    return <Typography variant='body2' color='textSecondary'>—</Typography>
  }

  const handleViewFilterSummary = (filtersJson) => {
    try {
      const parsed = typeof filtersJson === 'string' ? JSON.parse(filtersJson) : filtersJson
      setFilterSummaryData(parsed)
      setFilterSummaryOpen(true)
    } catch (e) {
      console.error('[Reports] failed to parse filters JSON', e)
    }
  }

  function formatDate(val) {
    if (!val) return '—'
    return new Date(val).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  return (
    <Box>
      {/* ── Report Filters ── */}
            <Paper variant='outlined' style={{ borderRadius: 8, marginBottom: theme.spacing(3) }}>
        <Box display='flex' alignItems='center' style={{ gap: 8, padding: '12px 16px', borderBottom: `1px solid ${theme.palette.divider}` }}>
          <FontAwesomeIcon icon={['fal', 'filter']} style={{ fontSize: 14 }} />
          <Typography variant='subtitle2' style={{ fontWeight: 600 }}>Report Filters</Typography>
        </Box>
        <Box p={2}>
          <Grid container spacing={2}>
            {/* Row 1 */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Creation Date From *'
                type='date'
                size='small'
                variant='outlined'
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.creationDateFrom}
                onChange={(e) => handleFilterChange('creationDateFrom', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Creation Date To *'
                type='date'
                size='small'
                variant='outlined'
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.creationDateTo}
                onChange={(e) => handleFilterChange('creationDateTo', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Submission Date From *'
                type='date'
                size='small'
                variant='outlined'
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.submissionDateFrom}
                onChange={(e) => handleFilterChange('submissionDateFrom', e.target.value)}
              />
            </Grid>
            {/* Row 2 */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Submission Date To *'
                type='date'
                size='small'
                variant='outlined'
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters.submissionDateTo}
                onChange={(e) => handleFilterChange('submissionDateTo', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label='Bill Type'
                size='small'
                variant='outlined'
                fullWidth
                value={filters.billType}
                onChange={(e) => handleFilterChange('billType', e.target.value)}
              >
                <MenuItem value=''>All</MenuItem>
                {BILL_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label='Master Bill Number'
                size='small'
                variant='outlined'
                fullWidth
                placeholder='Enter MBL No'
                value={filters.masterBillNo}
                onChange={(e) => handleFilterChange('masterBillNo', e.target.value)}
              />
            </Grid>
            {/* Row 3 */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label='Organisation'
                size='small'
                variant='outlined'
                fullWidth
                value={isAdmin ? (selectedOrgId ?? '') : (effectiveOrgId ?? '')}
                onChange={(e) => isAdmin && setSelectedOrgId(e.target.value || null)}
                disabled={!isAdmin}
              >
                <MenuItem value=''>All</MenuItem>
                {orgList.map((o) => (
                  <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label='Status Level'
                size='small'
                variant='outlined'
                fullWidth
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
              <MenuItem value=''>All</MenuItem>
                {FILING_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label='Export Format *'
                size='small'
                variant='outlined'
                fullWidth
                value={filters.exportFormat}
                onChange={(e) => handleFilterChange('exportFormat', e.target.value)}
              >
                <MenuItem value=''>Select Format</MenuItem>
                {EXPORT_FORMAT_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

                    {/* Action Buttons */}
          <Box mt={2} display='flex' alignItems='center' style={{ gap: 8 }}>
                        <Button
              variant='contained'
              color='primary'
              disableElevation
              onClick={handleGenerateReport}
              disabled={generating}
              style={{ textTransform: 'none', fontWeight: 600 }}
              startIcon={
                generating
                  ? <CircularProgress size={14} style={{ color: '#fff' }} />
                  : <FontAwesomeIcon icon={['fal', 'file-chart-column']} />
              }
            >
              {generating ? 'Submitting...' : 'Generate Report'}
            </Button>
            <Button
              variant='outlined'
              onClick={handleReset}
              disabled={generating}
              style={{ textTransform: 'none' }}
              startIcon={<FontAwesomeIcon icon={['fal', 'arrow-rotate-left']} />}
            >
              Reset
            </Button>
          </Box>
        </Box>
      </Paper>

                        {/* ── Report Requests Table ── */}
      <Paper variant='outlined' style={{ borderRadius: 8, marginBottom: theme.spacing(3) }}>
        <Box display='flex' alignItems='center' justifyContent='space-between' style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <Box display='flex' alignItems='center' style={{ gap: 10 }}>
            <FontAwesomeIcon icon={['fal', 'list']} style={{ fontSize: 14 }} />
            <Typography variant='subtitle2' style={{ fontWeight: 600 }}>Report Requests</Typography>
            {isPolling && (
              <Box
                display='flex'
                alignItems='center'
                style={{
                  gap: 5,
                  padding: '2px 10px',
                  borderRadius: 20,
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fde68a'
                }}
              >
                <CircularProgress size={9} style={{ color: '#b45309' }} />
                <Typography variant='caption' style={{ color: '#b45309', fontWeight: 500 }}>
                  Auto-refreshing every 5 min
                </Typography>
              </Box>
            )}
          </Box>
                    <Button
            variant='outlined'
            size='small'
            onClick={() => fetchReportList(page, pageSize, true)}
            disabled={refreshing}
            style={{ textTransform: 'none' }}
            startIcon={
              refreshing
                ? <CircularProgress size={12} />
                : <FontAwesomeIcon icon={['fal', 'rotate-right']} style={{ fontSize: 12 }} />
            }
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date Requested</TableCell>
                <TableCell>Filename</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Filter Summary</TableCell>
                <TableCell>Record Count</TableCell>
                <TableCell>File Size (MB)</TableCell>
                <TableCell>Download Link</TableCell>
                <TableCell align='center'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align='center' style={{ padding: 32 }}>
                    <Typography variant='body2' color='textSecondary'>
                      No report requests yet. Apply filters and click "Generate Report".
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reportRequests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{formatDate(request.createdDate || request.dateRequested)}</TableCell>
                    <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {request.fileName || request.filename || '—'}
                    </TableCell>
                    <TableCell>{renderStatusChip(request.status)}</TableCell>
                    <TableCell>
                      {request.filters
                        ? <Button variant='outlined' size='small' style={{ textTransform: 'none' }} onClick={() => handleViewFilterSummary(request.filters)}>View</Button>
                        : <Typography variant='body2' color='textSecondary'>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {request.numRecords != null ? String(request.numRecords) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {request.fileSizeMb != null ? `${Number(request.fileSizeMb).toFixed(2)} MB` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renderDownloadCell(request, handleDownload, handleDownloadErrorLog)}
                    </TableCell>
                    <TableCell align='center'>
                      <IconButton onClick={(e) => handleMenuOpen(e, request)} style={{ padding: '6px 10px' }}>
                        <FontAwesomeIcon icon={['fal', 'ellipsis-vertical']} style={{ fontSize: 14 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
                {/* Pagination */}
        <Box
          px={2} py={1}
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          style={{ borderTop: `1px solid`, borderColor: 'divider' }}
        >
          <Typography variant='caption' color='textSecondary'>
            {totalElements > 0
              ? `Showing ${page * pageSize + 1}–${Math.min(page * pageSize + pageSize, totalElements)} of ${totalElements}`
              : 'Status auto-refreshes every 5 seconds while reports are processing.'}
          </Typography>
          <Box display='flex' alignItems='center' style={{ gap: 8 }}>
            <Typography variant='body2' color='textSecondary'>Rows:</Typography>
            <Select
              value={pageSize}
              size='small'
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0) }}
            >
              {[10, 20, 50].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
            <Typography variant='body2' color='textSecondary'>
              Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
            </Typography>
            <IconButton size='small' onClick={() => setPage(0)} disabled={page === 0}>
              <FontAwesomeIcon icon={['fal', 'angles-left']} style={{ fontSize: 12 }} />
            </IconButton>
            <IconButton size='small' onClick={() => setPage((p) => p - 1)} disabled={page === 0}>
              <FontAwesomeIcon icon={['fal', 'chevron-left']} style={{ fontSize: 12 }} />
            </IconButton>
            <IconButton size='small' onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}>
              <FontAwesomeIcon icon={['fal', 'chevron-right']} style={{ fontSize: 12 }} />
            </IconButton>
            <IconButton size='small' onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>
              <FontAwesomeIcon icon={['fal', 'angles-right']} style={{ fontSize: 12 }} />
            </IconButton>
          </Box>
        </Box>
      </Paper>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleRegenerate(menuRow)}>
          <FontAwesomeIcon icon={['fal', 'arrows-rotate']} style={{ marginRight: 8, fontSize: 13 }} />
          Re-generate
        </MenuItem>
        {menuRow?.status?.toUpperCase() === 'FAIL' && (
          <MenuItem onClick={() => handleRetry(menuRow)}>
            <FontAwesomeIcon icon={['fal', 'rotate-right']} style={{ marginRight: 8, fontSize: 13 }} />
            Retry
          </MenuItem>
        )}
      </Menu>
      <ExcelRowLimitDialog
        open={excelWarnOpen}
        onProceed={() => {
          setExcelWarnOpen(false)
          triggerGenerate(pendingPayload)
        }}
        onCancel={() => {
          setExcelWarnOpen(false)
          setPendingPayload(null)
        }}
      />
      <FilterSummaryDialog
        open={filterSummaryOpen}
        data={filterSummaryData}
        onClose={() => setFilterSummaryOpen(false)}
      />
    </Box>
  )
}

function ExcelRowLimitDialog({ open, onProceed, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth>
      <DialogTitle>Export Warning</DialogTitle>
      <DialogContent>
        <Typography variant='body2'>
          ⚠ Excel supports a maximum of approximately 1,048,576 rows per worksheet. To export the
          complete dataset, consider using CSV format. If you proceed with XLSX, the report will be
          automatically split across multiple worksheets.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} size='small'>Cancel</Button>
        <Button onClick={onProceed} size='small' variant='contained' color='primary' disableElevation>
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function FilterSummaryDialog({ open, data, onClose }) {
  const LABELS = {
    module: 'Module',
    createdDateStart: 'Created Date Start',
    createdDateEnd: 'Created Date End',
    submittedDateStart: 'Submitted Date Start',
    submittedDateEnd: 'Submitted Date End',
    exportFormat: 'Export Format',
    billType: 'Bill Type',
    masterBillNo: 'Master Bill No',
    orgId: 'Organisation ID'
  }
  const FORMAT_VALUES = {
    exportFormat: { excel: 'Excel', csv: 'CSV' },
    billType: { M: 'Master Bill', H: 'House Bill' }
  }
  const formatValue = (key, val) => FORMAT_VALUES[key]?.[val] ?? String(val)
  const entries = data
    ? Object.entries(data).filter(([, v]) => v !== null && v !== '')
    : []
  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Filter Summary</DialogTitle>
      <DialogContent dividers>
        {entries.map(([key, val]) => (
          <Box key={key} display='flex' justifyContent='space-between' py={0.5}>
            <Typography variant='body2' color='textSecondary'>{LABELS[key] ?? key}</Typography>
            <Typography variant='body2' style={{ fontWeight: 500 }}>{formatValue(key, val)}</Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size='small'>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function WrappedReportsPage(props) {
  const breadcrumbNameMap = useBreadcrumbNameMap()
  const { showNotification, toastNode } = useMpciToast()
  return (
    <>
      <CngBasePage
        renderPage={() => <ReportsPage showNotification={showNotification} {...props} />}
        extraCngBreadcrumbNameMap={breadcrumbNameMap}
      />
      {toastNode}
    </>
  )
}

export { WrappedReportsPage as ReportsPage }
export default ReportsPage